import React, { useState, useEffect } from 'react';
import { Field, Label, Input, Radio, RadioGroup } from '@headlessui/react';
import clsx from 'clsx';

const Step2 = ({ next, back, data }) => {
  const [formData, setFormData] = useState(data);
  const [errors, setErrors] = useState({});
  const [socialMediaLinks, setSocialMediaLinks] = useState(data.socialMediaLinks || []);
  const [teamMembers, setTeamMembers] = useState([
    { name: data.teamMember1Name || '', age: data.teamMember1Age || '', email: data.teamMember1Email || '', telephone: data.teamMember1Telephone || '' },
    { name: data.teamMember2Name || '', age: data.teamMember2Age || '', email: data.teamMember2Email || '', telephone: data.teamMember2Telephone || '' }
  ]);

  useEffect(() => {
    setFormData(data);
    setSocialMediaLinks(data.socialMediaLinks || []);
    setTeamMembers([
      { name: data.teamMember1Name || '', age: data.teamMember1Age || '', email: data.teamMember1Email || '', telephone: data.teamMember1Telephone || '' },
      { name: data.teamMember2Name || '', age: data.teamMember2Age || '', email: data.teamMember2Email || '', telephone: data.teamMember2Telephone || '' }
    ]);
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors((prevErrors) => {
        const { [name]: removedError, ...rest } = prevErrors;
        return rest;
      });
    }

    // Validate telephone on change
    if (name === 'telephone') {
      if (!/^(?:\+|00)\d+$/.test(value)) {
        setErrors((prevErrors) => ({ ...prevErrors, telephone: 'Die Telefonnummer muss mit + oder 00 beginnen und nur Ziffern enthalten' }));
      } else {
        setErrors((prevErrors) => {
          const { telephone, ...rest } = prevErrors;
          return rest;
        });
      }
    }
  };

  const handleAddSocialMediaLink = () => {
    if (socialMediaLinks.length < 5) {
      setSocialMediaLinks([...socialMediaLinks, '']);
    }
  };

  const handleRemoveSocialMediaLink = (index) => {
    const updatedLinks = [...socialMediaLinks];
    updatedLinks.splice(index, 1);
    setSocialMediaLinks(updatedLinks);
  };

  const handleSocialMediaLinkChange = (index, value) => {
    const updatedLinks = [...socialMediaLinks];
    updatedLinks[index] = value;
    setSocialMediaLinks(updatedLinks);

    if (errors[`socialMediaLink-${index}`]) {
      setErrors((prevErrors) => {
        const { [`socialMediaLink-${index}`]: removedError, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  const handleAddTeamMember = () => {
    if (teamMembers.length < 2) {
      setTeamMembers([...teamMembers, { name: '', age: '', email: '', telephone: '' }]);
    }
  };

  const handleRemoveTeamMember = (index) => {
    const updatedMembers = [...teamMembers];
    updatedMembers.splice(index, 1);
    setTeamMembers(updatedMembers);
  };

  const handleTeamMemberChange = (index, field, value) => {
    const updatedMembers = [...teamMembers];
    updatedMembers[index][field] = value;
    setTeamMembers(updatedMembers);

    if (errors[`teamMember-${index}-${field}`]) {
      setErrors((prevErrors) => {
        const { [`teamMember-${index}-${field}`]: removedError, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  const handleRadioChange = (name, value) => {
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors((prevErrors) => {
        const { [name]: removedError, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Bitte gib den Namen der nominierten Person ein';
    if (!formData.age) newErrors.age = 'Bitte gib das Alter der nominierten Person ein';
    else if (!/^\d+$/.test(formData.age) || formData.age < 16 || formData.age > 24) newErrors.age = 'Das Alter muss eine Zahl zwischen 16 und 24 sein';
    if (!formData.email) newErrors.email = 'Bitte gib die E-Mail-Adresse der nominierten Person ein';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Die E-Mail-Adresse scheint nicht richtig zu sein';
    if (!formData.place) newErrors.place = 'Bitte gib die Stadt/Region der nominierten Person ein';
    if (formData.telephone && !/^(?:\+|00)\d+$/.test(formData.telephone)) {
      newErrors.telephone = 'Die Telefonnummer muss mit + oder 00 beginnen und nur Ziffern enthalten';
    }
    if (formData.socialMediaLink && !/^https?:\/\//i.test(formData.socialMediaLink)) {
      newErrors.socialMediaLink = 'Der Link muss mit http:// oder https:// beginnen';
    }
    socialMediaLinks.forEach((link, index) => {
      if (link && !/^https?:\/\//i.test(link)) {
        newErrors[`socialMediaLink-${index}`] = `Der Link ${index + 1} muss mit http:// oder https:// beginnen`;
      }
    });
    if (!formData.engagementAloneOrTeam) newErrors.engagementAloneOrTeam = 'Bitte gib an, ob nominierte Person alleine oder im Team engagiert ist';
    if (formData.engagementAloneOrTeam === 'team') {
      teamMembers.forEach((member, index) => {
        if (!member.name) newErrors[`teamMember-${index}-name`] = 'Bitte gib den vollständigen Namen des Teammitglieds ein';
        if (!member.age) newErrors[`teamMember-${index}-age`] = 'Bitte gib das Alter des Teammitglieds ein';
        else if (!/^\d+$/.test(member.age) || member.age < 16 || member.age > 24) newErrors[`teamMember-${index}-age`] = 'Das Alter des Teammitglieds muss eine Zahl zwischen 16 und 24 sein';
        if (!member.email) {
          newErrors[`teamMember-${index}-email`] = 'Die E-Mail-Adresse des Teammitglieds ist erforderlich';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
          newErrors[`teamMember-${index}-email`] = 'Die E-Mail-Adresse des Teammitglieds scheint nicht richtig zu sein';
        }
        if (member.telephone && !/^(?:\+|00)\d+$/.test(member.telephone)) {
          newErrors[`teamMember-${index}-telephone`] = 'Die Telefonnummer des Teammitglieds muss mit + oder 00 beginnen und nur Ziffern enthalten';
        }
      });
    }
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Transform teamMembers to individual fields
      const teamMemberFields = teamMembers.reduce((acc, member, index) => {
        const memberIndex = index + 1;
        acc[`teamMember${memberIndex}Name`] = member.name;
        acc[`teamMember${memberIndex}Age`] = member.age;
        if (member.email) acc[`teamMember${memberIndex}Email`] = member.email;
        if (member.telephone) acc[`teamMember${memberIndex}Telephone`] = member.telephone;
        return acc;
      }, {});

      next({ ...formData, socialMediaLinks, ...teamMemberFields });
    }
  };

  return (
    <div className="step text-white flex flex-col gap-12">
      <div className="flex flex-col gap-3">
        <h2 className="text-2xl font-bold">Informationen zu der nominierten Person</h2>

        {/* Name Input */}
        <Field>
          <Input
            placeholder="Vollständiger Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={clsx(
              'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
              'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
            )}
          />
          {errors.name && <p className="error px-6">{errors.name}</p>}
        </Field>

        {/* Age Input */}
        <Field>
          <Input
            placeholder="Alter (zwischen 16 und 24)"
            type="text"
            name="age"
            value={formData.age}
            onChange={handleChange}
            className={clsx(
              'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
              'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
            )}
            onKeyPress={(e) => {
              const regex = /^[0-9\b]+$/;
              if (!regex.test(e.key)) {
                e.preventDefault();
              }
            }}
          />
          {errors.age && <p className="error px-6">{errors.age}</p>}
        </Field>

        {/* Email Input */}
        <Field>
          <Input
            placeholder="E-Mail"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={clsx(
              'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
              'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
            )}
          />
          {errors.email && <p className="error px-6">{errors.email}</p>}
        </Field>

        {/* Place Input */}
        <Field>
          <Input
            placeholder="Stadt/Region"
            name="place"
            value={formData.place}
            onChange={handleChange}
            className={clsx(
              'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
              'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
            )}
          />
          {errors.place && <p className="error px-6">{errors.place}</p>}
        </Field>

        {/* Telephone Input (Optional) */}
        <Field>
          <Input
            placeholder="Telefon (optional)"
            name="telephone"
            value={formData.telephone}
            onChange={handleChange}
            className={clsx(
              'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
              'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
            )}
          />
          {errors.telephone && <p className="error px-6">{errors.telephone}</p>}
        </Field>

        {/* Social Media Input */}
        <Field>
          <Input
            placeholder="Social-Media-Profile oder Webseite (optional)"
            name="socialMediaLink"
            value={formData.socialMediaLink}
            onChange={handleChange}
            className={clsx(
              'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
              'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
            )}
          />
          {errors.socialMediaLink && <p className="error px-6">{errors.socialMediaLink}</p>}
          <p className="mt-5 text-white/80 text-sm/5 px-6">Bitte gib die URL zu einem Social-Media-Profil oder einer Website der nominierten Person an. Klicke auf “Links hinzufügen”, um weitere Profile hinzuzufügen.</p>
          <button onClick={handleAddSocialMediaLink} className="add-link-button flex gap-2 px-6 mt-4 hover:text-purple transition duration-100" disabled={socialMediaLinks.length >= 5}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <span className="font-semibold text-sm -mt-[2px]">Links hinzufügen</span>
          </button>
        </Field>
        <div className="mt-4 relative">
          {socialMediaLinks.map((link, index) => (
            <div key={index} className="flex items-center relative">
              <Field className="flex-1">
                <Input
                  name={`socialMediaLink-${index + 1}`}
                  placeholder={`Social-Media-Link-${index + 1}`}
                  value={link}
                  onChange={(e) => handleSocialMediaLinkChange(index, e.target.value)}
                  className={clsx(
                    'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                    'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
                  )}
                />
                {errors[`socialMediaLink-${index}`] && <p className="error px-6">{errors[`socialMediaLink-${index}`]}</p>}
              </Field>
              <button
                className="ml-2 text-white/80 hover:text-purple focus:outline-none absolute right-6 top-[25px]"
                onClick={() => handleRemoveSocialMediaLink(index)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        {/* Engagement Alone or Team */}
        <Field className="pt-12 space-y-4">
          <Label><h2 className="text-xl/7 font-bold pr-6 md:pr-40">Engagiert sich die nominierte Person allein oder als Teil eines Teams?</h2></Label>
          <RadioGroup
            value={formData.engagementAloneOrTeam}
            onChange={(value) => handleRadioChange('engagementAloneOrTeam', value)}
            className="space-y-4"
          >
            {[
              { id: 'alone', label: 'Allein', description: '', value: 'alone' },
              { id: 'team', label: 'Teil eines Teams', description: '', value: 'team' },
            ].map((option) => (
              <Radio
                key={option.id}
                value={option.value}
                className="group relative flex cursor-pointer bg-white/5 py-6 px-6 text-white transition focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white data-[checked]:bg-white/10 hover:bg-white/10"
              >
                <div className="flex w-full items-center justify-between relative">
                  <div className="min-w-0 flex-1 text-sm/7">
                    <Label className="text-lg font-semibold">{option.label}</Label>
                    <p id={`${option.id}-description`} className="text-sm text-white/80">{option.description}</p>
                  </div>
                  <div className="absolute right-0 size-6 rounded-full bg-white/50 text-white transition group-data-[checked]:bg-purple mb-1">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="size-4 fill-purple text-white opacity-0 transition group-data-[checked]:opacity-100 relative top-1 left-1">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </div>
                </div>
              </Radio>
            ))}
            {errors.engagementAloneOrTeam && <p className="error px-6">{errors.engagementAloneOrTeam}</p>}
          </RadioGroup>
        </Field>

        {/* Team Members (if selected) */}
        {formData.engagementAloneOrTeam === 'team' && (
          <div className="pt-4 relative space-y-6">
            <p className="text-white/80 text-sm/5 px-6 max-w-xl flex gap-1 items-start">
              Falls sich die nominierte Person im Rahmen eines Teams engagiert, gib bitte die Namen und Kontaktdaten von bis zu zwei weiteren Teammitgliedern an.
            </p>
            {teamMembers.map((member, index) => (
              <div key={index} className="space-y-4 border-t-2 border-purple pt-4 relative">
                <Field>
                  <Input
                    placeholder={`Vollständiger Name (${index + 2}. Team-Mitglied)`}
                    name={`teamMember-${index}-name`}
                    value={member.name}
                    onChange={(e) => handleTeamMemberChange(index, 'name', e.target.value)}
                    className={clsx(
                      'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                      'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
                    )}
                  />
                  {errors[`teamMember-${index}-name`] && <p className="error px-6">{errors[`teamMember-${index}-name`]}</p>}
                </Field>

                <Field>
                  <Input
                    placeholder="Alter (zwischen 16 und 24)"
                    type="text"
                    name={`teamMember-${index}-age`}
                    value={member.age}
                    onChange={(e) => handleTeamMemberChange(index, 'age', e.target.value)}
                    className={clsx(
                      'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                      'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
                    )}
                    onKeyPress={(e) => {
                      const regex = /^[0-9\b]+$/;
                      if (!regex.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  {errors[`teamMember-${index}-age`] && <p className="error px-6">{errors[`teamMember-${index}-age`]}</p>}
                </Field>

                <Field>
                  <Input
                    placeholder="E-Mail"
                    type="email"
                    name={`teamMember-${index}-email`}
                    value={member.email}
                    onChange={(e) => handleTeamMemberChange(index, 'email', e.target.value)}
                    className={clsx(
                      'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                      'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
                    )}
                  />
                  {errors[`teamMember-${index}-email`] && <p className="error px-6">{errors[`teamMember-${index}-email`]}</p>}
                </Field>

                <Field>
                  <Input
                    placeholder="Telefon (optional)"
                    name={`teamMember-${index}-telephone`}
                    value={member.telephone}
                    onChange={(e) => handleTeamMemberChange(index, 'telephone', e.target.value)}
                    className={clsx(
                      'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                      'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
                    )}
                  />
                  {errors[`teamMember-${index}-telephone`] && <p className="error px-6">{errors[`teamMember-${index}-telephone`]}</p>}
                </Field>

                {teamMembers.length > 1 && (
                  <button
                    className="ml-2 text-white/80 hover:text-purple focus:outline-none absolute right-6 top-[25px]"
                    onClick={() => handleRemoveTeamMember(index)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            {teamMembers.length < 2 && (
              <button onClick={handleAddTeamMember} className="add-link-button flex gap-2 px-6 mt-4 hover:text-purple transition duration-100">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <span className="font-semibold text-sm -mt-[2px]">Weitere Person hinzufügen</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Progress Navigation */}
      <nav className="progress-nav">
        <div className="inner flex h-full items-center justify-between">
          <button onClick={back} className="flex gap-3 items-center hover:text-purple">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
            </svg>
            <span className="block -mt-1 font-semibold tracking-wide">Zurück</span>
          </button>
          <button onClick={handleSubmit} className="primary-form">
            <span className="font-semibold tracking-wide -mt-0.5 block text-lg">Weiter</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Step2;
